import './style/bolmateSearchInput.scss';

import {Icon} from '@iconify/react';

import React, {
    CSSProperties,
    DetailedHTMLProps,
    FocusEventHandler,
    InputHTMLAttributes,
    KeyboardEventHandler,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';

import classNames from 'classnames';

import {ErrorType} from '@dvrd/dvr-controls';

import {BolmateControlSize} from '../../../util/types';

interface Props {
    value?: string;
    onChange: (value: string) => void;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    placeholder?: string;
    label?: string;
    className?: string;
    inputClassName?: string;
    debounce?: number;
    disabled?: boolean;
    autoFocus?: boolean;
    autoSelect?: boolean;
    fullWidth?: boolean;
    error?: ErrorType;
    id?: string;
    style?: CSSProperties;
    size?: BolmateControlSize;
    /** Per-character allow-list (e.g. /[A-Za-z0-9]/) — disallowed characters
     *  are dropped as they are typed, so they never linger in the field. */
    allowCharReg?: RegExp;
    inputProps?: Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'crossOrigin'>;
}

export default function BolmateSearchInput(props: Props) {
    const {
        value,
        onChange,
        onFocus,
        onBlur,
        onKeyDown,
        placeholder = 'Zoeken...',
        label,
        className,
        inputClassName,
        debounce = 0,
        disabled,
        autoFocus,
        autoSelect,
        fullWidth,
        error,
        id,
        style,
        size = 'normal',
        allowCharReg,
        inputProps
    } = props;

    const [internal, setInternal] = useState(value ?? '');
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const inputRef = useRef<HTMLInputElement>(null);
    const hasError = !!error;

    // Sync from parent when value prop changes externally
    useEffect(() => {
        if (value !== undefined) {
            setInternal(value);
        }
    }, [value]);

    const emitChange = useCallback(
        (v: string) => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (debounce > 0) {
                timerRef.current = setTimeout(() => onChangeRef.current(v), debounce);
            } else {
                onChangeRef.current(v);
            }
        },
        [debounce]
    );

    // Cleanup timer on unmount
    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        []
    );

    useEffect(() => {
        if (!disabled && autoFocus) inputRef.current?.focus();
    }, [autoFocus, disabled]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let v = e.target.value;
        if (allowCharReg) v = [...v].filter(char => allowCharReg.test(char)).join('');
        setInternal(v);
        emitChange(v);
    };

    const handleClear = () => {
        setInternal('');
        if (timerRef.current) clearTimeout(timerRef.current);
        onChangeRef.current('');
        inputRef.current?.focus();
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (autoSelect) {
            e.target.selectionStart = 0;
            e.target.selectionEnd = internal.length;
        }
        onFocus?.(e);
    };

    return (
        <div
            id={id}
            className={classNames(
                'bolmate-search-input-container',
                `size-${size}`,
                disabled && 'disabled',
                hasError && 'error',
                fullWidth && 'full',
                className
            )}
            style={style}
        >
            {label && <span className='bolmate-search-input-label'>{label}</span>}
            <div className='bolmate-search-input'>
                <Icon icon='mdi:magnify' className='bolmate-search-icon' />
                <input
                    ref={inputRef}
                    type='text'
                    placeholder={placeholder}
                    value={internal}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    disabled={disabled}
                    className={inputClassName}
                    {...inputProps}
                />
                {internal && !disabled && (
                    <button type='button' className='bolmate-search-clear' onClick={handleClear}>
                        <Icon icon='mdi:close' />
                    </button>
                )}
            </div>
            {hasError && <span className='bolmate-search-input-error'>{error}</span>}
        </div>
    );
}
